import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Typography } from "@mui/material";
import Swal from "sweetalert2";
import VisiblityonIconSVGModel from "assets/images/visiblityonIconSVGModel.svg";
import VisiblityoffIconSVGModel from "assets/images/visiblityoffIconSVGModel.svg";
import {
  ActivityReasonActionBar,
  ModalEditActivityReason,
} from "components/molecules";
import { openAdminModal } from "store/slices/common/adminModal.slice";
import { Button, DataGridColumnHeader, TableContainer } from "components/ui";
import { CustomLoader, ReadMore, VisiblitySwitch } from "components/atoms";
import GlobalPagination from "utils/GlobalPagination";
import checkAccess from "utils/checkAccess";
import {
  GetAllReasonsAction,
  UpdateReasonAction,
} from "store/middlewares/dataManagement/activityReason";

function BlockReason() {
  const dispatch = useDispatch();
  const { activityReason, activityReasonLoading } = useSelector(
    (state) => state.reason,
  );
  const [filterObj, setFilterObj] = useState(null);

  useEffect(() => {
    setFilterObj({
      page: 1,
      pageSize: GlobalPagination.pageSize,
      type: "blocked",
    });
  }, []);

  useEffect(() => {
    if (filterObj) {
      dispatch(GetAllReasonsAction(filterObj));
    }
  }, [dispatch, filterObj]);

  const columns = [
    {
      id: "No.",
      accessorFn: (row) => row.is_active,
      header: ({ column }) => (
        <div className="flex justify-center">
          <DataGridColumnHeader title="No." column={column} />
        </div>
      ),
      enableColumnFilter: false,
      enableSorting: false,
      size: 40,
      cell: ({ row }) => (
        <div className="flex justify-center">
          <Typography variant="p">
            {activityReason?.pageSize * (activityReason?.currentPage - 1) +
              row.index +
              1}
          </Typography>
        </div>
      ),
    },
    {
      id: "enName",
      accessorFn: (row) => row.en_name,
      header: ({ column }) => (
        <DataGridColumnHeader title="Title (English)" column={column} />
      ),
      enableSorting: true,
      size: 180,
      accessorKey: "en_name",
      enableColumnFilter: false,
      cell: ({ row }) => (
        <span className="text-foreground font-normal max-w-full break-all">
          <ReadMore comment={row.original.en_name ?? "N/A"} maxChar={50} />
        </span>
      ),
    },
    {
      id: "arName",
      accessorFn: (row) => row.ar_name,
      header: ({ column }) => (
        <div className="flex justify-end">
          <DataGridColumnHeader title="Title (Arabic)" column={column} />
        </div>
      ),
      enableSorting: false,
      size: 180,
      accessorKey: "ar_name",
      enableColumnFilter: false,
      cell: ({ row }) => (
        <span className="text-foreground font-normal max-w-full break-all flex justify-end">
          <ReadMore comment={row.original.ar_name || "N/A"} maxChar={50} />
        </span>
      ),
    },
    {
      id: "is_active",
      accessorFn: (row) => row.is_active,
      header: ({ column }) => (
        <DataGridColumnHeader title="Visiblity" column={column} />
      ),
      enableColumnFilter: false,
      enableSorting: true,
      size: 80,
      accessorKey: "is_active",
      cell: ({ row }) => (
        <VisiblitySwitch
          checked={row.original.is_active}
          onChange={() =>
            Swal.fire({
              title: `Are you sure you want to change the visibility to ${
                row.original.is_active ? "Off" : "On"
              }?`,
              imageUrl: row.original.is_active
                ? VisiblityoffIconSVGModel
                : VisiblityonIconSVGModel,
              imageWidth: 100,
              imageHeight: 100,
              showCancelButton: true,
              confirmButtonText: "Confirm",
              customClass: {
                popup: "custom-swal-popup",
                title: "Swal-title-class",
                confirmButton: "custom-swal-confirm",
                cancelButton: "custom-swal-cancel",
              },
            }).then((result) => {
              if (result.isConfirmed) {
                dispatch(
                  UpdateReasonAction({
                    id: row.original.id,
                    isActive: !row.original.is_active,
                    onSuccess: () =>
                      setFilterObj((prev) => ({ ...prev, page: 1 })),
                  }),
                );
              }
            })
          }
        />
      ),
    },
    (checkAccess("update_block_reason") ||
      checkAccess("delete_block_reason")) && {
      id: "action",
      accessorFn: (row) => row.is_active,
      header: ({ column }) => (
        <div className="flex justify-center">
          <DataGridColumnHeader title="Action" column={column} />
        </div>
      ),
      enableColumnFilter: false,
      enableSorting: false,
      size: 80,
      cell: ({ row }) => (
        <ActivityReasonActionBar
          row={row.original}
          type="blocked"
          onSuccess={() => setFilterObj((prev) => ({ ...prev, page: 1 }))}
        />
      ),
    },
  ].filter(Boolean);

  return (
    <div>
      {!activityReasonLoading ? (
        <div className="mt-4">
          <TableContainer
            columns={columns}
            isGlobalFilter={false}
            isPagination={false}
            hasSearch={true}
            onSearch={(val) => {
              setFilterObj((prev) => ({
                ...prev,
                page: 1,
                search: val === "" ? null : val.trim(),
              }));
            }}
            onUpdatePagination={(size, page) => {
              setFilterObj((prev) => ({ ...prev, page, pageSize: size }));
            }}
            paginationState={{
              pageIndex: filterObj?.page - 1,
              pageSize: filterObj?.pageSize,
            }}
            recordCount={activityReason?.total ?? 0}
            data={activityReason?.data ?? []}
            title="Block Reason" // ✅ changed
            totalPages={activityReason?.totalPages ?? 0}
            customSorting={true}
            onUpdateSort={(val) => {
              if (val.length > 0) {
                const { id, desc } = val[0];
                setFilterObj((prev) => ({
                  ...prev,
                  sortOn: id,
                  sortBy: desc ? "desc" : "asc",
                  page: 1,
                  pageSize: GlobalPagination.pageSize,
                }));
              } else {
                setFilterObj((prev) => ({
                  ...prev,
                  page: 1,
                  sortOn: null,
                  sortBy: null,
                }));
              }
            }}
            extraButton={
              checkAccess("create_block_reason") && ( // ✅ changed
                <Button
                  onClick={() =>
                    dispatch(
                      openAdminModal({
                        title: "Add Block Reason", // ✅ changed
                        content: (
                          <ModalEditActivityReason
                            row={null}
                            type="blocked" // ✅ changed
                            onSuccess={() =>
                              setFilterObj((prev) => ({ ...prev, page: 1 }))
                            }
                          />
                        ),
                      }),
                    )
                  }
                >
                  Add Block Reason {/* ✅ changed */}
                </Button>
              )
            }
          />
        </div>
      ) : (
        <CustomLoader />
      )}
    </div>
  );
}

export default BlockReason;
